import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import Icon from "../../components/Icon";
import {
  NPS_BUCKET_LABEL,
  RESPONSE_PAGE_SIZE,
  SURVEY_ACTIONS,
  defaultRange,
  formatDateTime,
  formatScore,
  useSurveyRequest
} from "./api";
import { isNpsSummary, scaleBars } from "./summary";
import "./style.scss";

const DEFAULT_MONTHS_BACK = 3;

/*
 * "Use CSAT to monitor the application experience", and the quarterly NPS behind
 * "Customer Loyalty Growth: track and measure loyalty trends over time".
 *
 * The page answers two questions and keeps them apart on purpose. The cards and the histogram
 * say what the score is. The table underneath says why: a CSAT of 3.4 is not something anyone
 * can act on, but the six sentences customers typed next to it are.
 *
 * Nothing here can change a survey. The questions live in be-notification's config so that the
 * app renders whatever it is handed, and a question edited from this screen would silently
 * change the meaning of every score already recorded against it.
 */
function SurveyInsight() {
  const requestSurvey = useSurveyRequest();

  const [surveys, SETxSurveys] = useState([]);
  const [activeCode, SETxActiveCode] = useState("");
  const [range, SETxRange] = useState(() => defaultRange(DEFAULT_MONTHS_BACK));
  const [appliedRange, SETxAppliedRange] = useState(() => defaultRange(DEFAULT_MONTHS_BACK));
  const [summary, SETxSummary] = useState(null);
  const [responses, SETxResponses] = useState([]);
  const [questions, SETxQuestions] = useState([]);
  const [pagination, SETxPagination] = useState(null);
  const [page, SETxPage] = useState(1);
  const [loading, SETxLoading] = useState(false);
  const [error, SETxError] = useState(null);

  // A reply that arrives after the operator has moved on is dropped rather than written over
  // newer state - switching tabs quickly used to leave one survey's score above another's rows.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const describeError = requestError =>
    requestError && requestError.status === 404
      ? "ไม่พบแบบสอบถามนี้ใน be-notification"
      : (requestError && requestError.message) || "เรียกข้อมูลไม่สำเร็จ";

  /* Which surveys exist. Served, not hard-coded, so a new survey needs no dashboard release. */
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const result = await requestSurvey(SURVEY_ACTIONS.list);
        if (!live || result.signedOut) {
          return;
        }
        const list = (result.data && result.data.data) || [];
        SETxSurveys(list);
        SETxActiveCode(current => current || (list[0] && list[0].code) || "");
      } catch (requestError) {
        if (live) {
          SETxError(describeError(requestError));
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [requestSurvey]);

  const fetchSurvey = useCallback(
    async (code, activeRange, activePage) => {
      if (!code) {
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

      SETxLoading(true);
      SETxError(null);
      try {
        const params = { from: activeRange.from, to: activeRange.to };
        const [summaryResult, responseResult] = await Promise.all([
          requestSurvey(SURVEY_ACTIONS.summary, { code, query: params }),
          requestSurvey(SURVEY_ACTIONS.responses, {
            code,
            query: { ...params, page: activePage, limit: RESPONSE_PAGE_SIZE }
          })
        ]);

        if (!isCurrent() || summaryResult.signedOut || responseResult.signedOut) {
          return;
        }
        SETxSummary(summaryResult.data);
        SETxResponses((responseResult.data && responseResult.data.data) || []);
        SETxQuestions((responseResult.data && responseResult.data.questions) || []);
        SETxPagination((responseResult.data && responseResult.data.pagination) || null);
      } catch (requestError) {
        if (!isCurrent()) {
          return;
        }
        SETxSummary(null);
        SETxResponses([]);
        SETxPagination(null);
        SETxError(describeError(requestError));
      } finally {
        if (isCurrent()) {
          SETxLoading(false);
        }
      }
    },
    [requestSurvey]
  );

  useEffect(() => {
    fetchSurvey(activeCode, appliedRange, page);
  }, [fetchSurvey, activeCode, appliedRange, page]);

  const activeSurvey = useMemo(
    () => surveys.find(survey => survey.code === activeCode) || null,
    [surveys, activeCode]
  );

  const bars = useMemo(() => scaleBars(activeSurvey, summary), [activeSurvey, summary]);

  const isNps = isNpsSummary(summary);
  const totalPages = (pagination && pagination.totalPages) || 0;

  const onApply = event => {
    event.preventDefault();
    SETxPage(1);
    SETxAppliedRange({ ...range });
  };

  const onReset = () => {
    const fresh = defaultRange(DEFAULT_MONTHS_BACK);
    SETxRange(fresh);
    SETxPage(1);
    SETxAppliedRange(fresh);
  };

  const answerText = (response, question) => {
    const value = (response.answers || {})[question.id];
    if (value === undefined || value === null || value === "") {
      return "-";
    }
    return Array.isArray(value) ? value.join(", ") : String(value);
  };

  return (
    <Fragment>
      {loading ? (
        <div className="survey-progress">
          <CircularProgress />
          <p className="survey-progress-text">กำลังโหลดผลแบบสอบถาม...</p>
        </div>
      ) : null}

      <div className="survey-container">
        <div className="survey-topbar">
          <div className="survey-titles">
            <h2 className="survey-heading">CSAT / NPS</h2>
            <p className="survey-subheading">
              ผลแบบสอบถามที่ลูกค้าตอบจากแอป Principal TH — คะแนน แนวโน้ม และความเห็นรายคน
            </p>
          </div>
          {/*
            * The survey itself goes out as a notification, so "ask more customers" is the
            * compose form rather than anything on this page.
            */}
          <Link className="survey-send" to="/PushNoti_Compose">
            <Icon name="send" size={18} />
            ส่งแบบสอบถาม
          </Link>
        </div>

        <div className="survey-tabs">
          {surveys.map(survey => (
            <button
              type="button"
              key={survey.code}
              className={survey.code === activeCode ? "survey-tab active" : "survey-tab"}
              onClick={() => {
                SETxPage(1);
                SETxActiveCode(survey.code);
              }}
            >
              {survey.titleTh || survey.code}
            </button>
          ))}
        </div>

        {activeSurvey ? (
          <p className="survey-code-line">
            <span className="survey-code">{activeSurvey.code}</span>
            ถามซ้ำได้ทุก {activeSurvey.cooldownDays} วัน · {(activeSurvey.questions || []).length} คำถาม
          </p>
        ) : null}

        <form className="survey-filter" onSubmit={onApply}>
          <div className="survey-filter-field">
            <span className="survey-filter-label">ตั้งแต่วันที่</span>
            <input
              type="date"
              value={range.from}
              onChange={event => SETxRange({ ...range, from: event.target.value })}
            />
          </div>
          <div className="survey-filter-field">
            <span className="survey-filter-label">ถึงวันที่</span>
            <input
              type="date"
              value={range.to}
              onChange={event => SETxRange({ ...range, to: event.target.value })}
            />
          </div>
          <div className="survey-filter-actions">
            <button type="submit" className="survey-apply">
              <Icon name="filter" size={18} />
              ดูผล
            </button>
            <button type="button" className="survey-reset" onClick={onReset}>
              <Icon name="refresh" size={18} />
              ล้างช่วงวันที่
            </button>
          </div>
        </form>

        {error ? <div className="survey-error">{error}</div> : null}

        <div className="survey-cards">
          <div className="survey-card">
            <span className="survey-card-label">จำนวนผู้ตอบ</span>
            <span className="survey-card-value">{(summary && summary.total) || 0}</span>
          </div>
          <div className="survey-card">
            <span className="survey-card-label">คะแนนเฉลี่ย</span>
            <span className="survey-card-value">
              {formatScore(summary && summary.averageScore, summary && summary.total)}
            </span>
          </div>
          {isNps ? (
            <Fragment>
              <div className="survey-card accent">
                <span className="survey-card-label">NPS</span>
                <span className="survey-card-value">{summary.nps}</span>
                <span className="survey-card-note">promoter − detractor</span>
              </div>
              <div className="survey-card">
                <span className="survey-card-label">Promoter (9-10)</span>
                <span className="survey-card-value">{summary.promoters}</span>
              </div>
              <div className="survey-card">
                <span className="survey-card-label">Passive (7-8)</span>
                <span className="survey-card-value">{summary.passives}</span>
              </div>
              <div className="survey-card">
                <span className="survey-card-label">Detractor (0-6)</span>
                <span className="survey-card-value">{summary.detractors}</span>
              </div>
            </Fragment>
          ) : null}
        </div>

        {bars.length > 0 ? (
          <div className="survey-histogram">
            <p className="survey-section-title">การกระจายคะแนน</p>
            {bars.map(bar => (
              <div className="survey-bar-row" key={bar.score}>
                <span className="survey-bar-score">{bar.score}</span>
                <span className="survey-bar-track">
                  <span className="survey-bar-fill" style={{ width: `${bar.percent}%` }} />
                </span>
                <span className="survey-bar-count">{bar.count}</span>
              </div>
            ))}
          </div>
        ) : null}

        <p className="survey-section-title">ความเห็นรายคน</p>

        <div className="survey-rows">
          {responses.map(response => (
            <div className="survey-row" key={response.id}>
              <div className="survey-row-score">
                <span className="survey-score-value">{response.score}</span>
                {response.bucket ? (
                  <span className={`survey-bucket ${response.bucket.toLowerCase()}`}>
                    {NPS_BUCKET_LABEL[response.bucket] || response.bucket}
                  </span>
                ) : null}
              </div>

              <div className="survey-row-body">
                <p className="survey-row-comment">
                  {response.comment || <span className="survey-row-muted">ไม่ได้เขียนความเห็น</span>}
                </p>
                {questions
                  .filter(question => question.type !== "SCALE" && question.type !== "TEXT")
                  .map(question => (
                    <p className="survey-row-answer" key={question.id}>
                      <span className="survey-row-question">{question.titleTh}</span>
                      {answerText(response, question)}
                    </p>
                  ))}
              </div>

              <div className="survey-row-meta">
                <span>{formatDateTime(response.createdAt)}</span>
                <span className="survey-row-muted">
                  {response.channel || "-"}
                  {response.appVersion ? ` · v${response.appVersion}` : ""}
                </span>
              </div>
            </div>
          ))}

          {!loading && responses.length === 0 ? (
            <div className="survey-empty">ยังไม่มีผู้ตอบแบบสอบถามในช่วงวันที่นี้</div>
          ) : null}
        </div>

        {totalPages > 1 ? (
          <div className="survey-pager">
            <button type="button" disabled={page <= 1} onClick={() => SETxPage(page - 1)}>
              ก่อนหน้า
            </button>
            <span>
              หน้า {pagination.page} / {totalPages} (ทั้งหมด {pagination.total} รายการ)
            </span>
            <button type="button" disabled={page >= totalPages} onClick={() => SETxPage(page + 1)}>
              ถัดไป
            </button>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
}

export default SurveyInsight;
